<template>
  <div id="payment-adjustment-details" class="align-start fill-height">
    <i-overlay v-if="fetching" text="Loading Payment Adjustment details..." />

    <v-row v-else class="g-container" no-gutters>
      <v-col>
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-col class="mt-2 pa-0">
            <v-card class="rounded-2 mb-6">
              <v-row no-gutters align="center" class="p-div-title py-3 px-3">
                <v-col cols="12">
                  <h3 class="g-heading-m p-title mr-2">
                    Reference Number
                    {{ `#${paymentAdjustment.referenceNumber}` }}
                  </h3>
                </v-col>
              </v-row>

              <div class="pa-3">
                <v-row no-gutters>
                  <v-col cols="6" class="py-1">
                    <span>{{ isILR ? 'CUSTOMER' : 'RETAILER' }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span v-if="paymentAdjustment.store">{{
                      paymentAdjustment.store.name
                    }}</span>
                    <span v-else>{{ paymentAdjustment.storeName }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREDIT TO</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ paymentAdjustment.creditTo }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREATED BY</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{ paymentAdjustment.raisedBy }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREATED DATE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span>{{
                      $filters.formatDateTime12h(paymentAdjustment.createdAt)
                    }}</span>
                  </v-col>
                  <v-col cols="6" class="py-1">
                    <span>CREDIT PAYMENT TYPE</span>
                  </v-col>
                  <v-col cols="6" class="py-1 pl-4">
                    <span
                      ><i-payment-type
                        v-if="paymentAdjustment.paymentType"
                        :paymentType="paymentAdjustment.paymentType"
                        :textOnly="true"
                    /></span>
                  </v-col>
                  <template v-if="paymentAdjustment.linkedReferenceNumber">
                    <v-col cols="6" class="py-1">
                      <span>RELATED TO</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <router-link
                        v-if="createLinkForRelatedIssue(paymentAdjustment)"
                        class="i-a-link"
                        :to="createLinkForRelatedIssue(paymentAdjustment)"
                      >
                        <span>{{
                          $filters.text(paymentAdjustment.linkedReferenceNumber)
                        }}</span>
                      </router-link>
                      <span v-else>{{
                        $filters.text(paymentAdjustment.linkedReferenceNumber)
                      }}</span>
                    </v-col>
                  </template>
                </v-row>

                <v-divider class="mt-3 mb-2"></v-divider>

                <v-row v-if="paymentAdjustment.comment" no-gutters class="py-3">
                  <v-col cols="12">
                    <span><strong>COMMENT</strong></span>
                    <div class="g-border-radius-s i-message">
                      <span>{{ paymentAdjustment.comment }}</span>
                    </div>
                  </v-col>
                </v-row>

                <v-divider
                  v-if="paymentAdjustment.comment"
                  class="mb-4"
                ></v-divider>

                <div class="g-bold g-border-radius-s i-status-block-gray pa-2">
                  <v-row no-gutters>
                    <v-col cols="10" class="text-right">
                      <span>TOTAL (EX TAX):</span>
                    </v-col>
                    <v-col cols="2" class="text-right pl-2">
                      <span>{{
                        $filters.currency(paymentAdjustment.totalExTax)
                      }}</span>
                    </v-col>
                    <v-col
                      v-if="usesAustralianTaxLabels && taxDisplayPolicy.showWet"
                      cols="10"
                      class="text-right"
                    >
                      <span>{{ taxDisplayPolicy.wetLabel }}:</span>
                    </v-col>
                    <v-col
                      v-if="usesAustralianTaxLabels && taxDisplayPolicy.showWet"
                      cols="2"
                      class="text-right pl-2"
                    >
                      <span>{{
                        $filters.currency(paymentAdjustment.totalWet)
                      }}</span>
                    </v-col>
                    <v-col
                      v-if="usesAustralianTaxLabels"
                      cols="10"
                      class="text-right"
                    >
                      <span
                        >{{
                          taxDisplayPolicy.primaryTaxLabel.toUpperCase()
                        }}:</span
                      >
                    </v-col>
                    <v-col
                      v-if="usesAustralianTaxLabels"
                      cols="2"
                      class="text-right pl-2"
                    >
                      <span>{{
                        $filters.currency(paymentAdjustment.totalGst)
                      }}</span>
                    </v-col>
                  </v-row>

                  <div class="i-total-divider"></div>

                  <v-row no-gutters>
                    <v-col cols="10" class="text-right">
                      <span>{{
                        usesAustralianTaxLabels ? 'TOTAL (INC TAX):' : 'TOTAL:'
                      }}</span>
                    </v-col>
                    <v-col cols="2" class="text-right pl-2">
                      <span>{{ $filters.currency(displayTotal) }}</span>
                    </v-col>
                  </v-row>
                </div>
                <div class="text-center pt-2">
                  <v-btn
                    class="i-btn-block-primary g-full-width"
                    @click="fetchPDF()"
                    :loading="fetchingPDF"
                  >
                    <span>Export PDF</span>
                    <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-card>
          </v-col>
        </template>

        <template v-else>
          <v-card class="rounded-2 mt-4">
            <v-row no-gutters align="center" class="p-div-title pt-3 pb-3 px-4">
              <v-col cols="12" md="6">
                <h3 class="g-heading-m p-title mr-2">
                  Reference Number {{ `#${paymentAdjustment.referenceNumber}` }}
                </h3>
              </v-col>

              <v-col cols="12" md="6" class="text-right">
                <v-btn
                  class="i-btn-block-primary"
                  @click="fetchPDF()"
                  :loading="fetchingPDF"
                >
                  <span>Export PDF</span>
                  <v-icon color="white" class="ml-1">mdi-file-pdf-box</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-row class="pa-4">
              <v-col cols="12" md="8" lg="8" class="py-0">
                <v-simple-table class="pa-0">
                  <tr>
                    <td>{{ isILR ? 'Customer' : 'Retailer' }}</td>
                    <td>
                      <span v-if="paymentAdjustment.store">{{
                        paymentAdjustment.store.name
                      }}</span>
                      <span v-else>{{ paymentAdjustment.storeName }}</span>
                    </td>
                  </tr>

                  <tr>
                    <td>Credit to</td>
                    <td>{{ paymentAdjustment.creditTo }}</td>
                  </tr>

                  <tr>
                    <td>Created by</td>
                    <td>{{ paymentAdjustment.raisedBy }}</td>
                  </tr>

                  <tr>
                    <td>Created Date</td>
                    <td>
                      {{
                        $filters.formatDateTime12h(paymentAdjustment.createdAt)
                      }}
                    </td>
                  </tr>

                  <tr>
                    <td>Credit Payment Type</td>
                    <td>
                      <i-payment-type
                        v-if="paymentAdjustment.paymentType"
                        :paymentType="paymentAdjustment.paymentType"
                        :textOnly="true"
                      />
                    </td>
                  </tr>

                  <template v-if="paymentAdjustment.linkedReferenceNumber">
                    <tr>
                      <td>Related to</td>
                      <td>
                        <router-link
                          v-if="createLinkForRelatedIssue(paymentAdjustment)"
                          class="i-a-link"
                          :to="createLinkForRelatedIssue(paymentAdjustment)"
                        >
                          <span>{{
                            $filters.text(
                              paymentAdjustment.linkedReferenceNumber
                            )
                          }}</span>
                        </router-link>
                        <span v-else>{{
                          $filters.text(paymentAdjustment.linkedReferenceNumber)
                        }}</span>
                      </td>
                    </tr>
                  </template>
                </v-simple-table>
              </v-col>

              <v-spacer></v-spacer>

              <v-col cols="12" md="4" lg="4" class="py-0 text-right">
                <v-row no-gutters>
                  <v-col cols="12" class="mt-1">
                    <div
                      class="g-bold g-border-radius-s i-status-block-gray pa-2"
                    >
                      <v-row no-gutters>
                        <v-col cols="9" class="text-right">
                          <span>TOTAL (EX TAX):</span>
                        </v-col>
                        <v-col cols="3" class="text-right pl-2">
                          <span>{{
                            $filters.currency(paymentAdjustment.totalExTax)
                          }}</span>
                        </v-col>
                        <v-col
                          v-if="
                            usesAustralianTaxLabels && taxDisplayPolicy.showWet
                          "
                          cols="9"
                          class="text-right"
                        >
                          <span>{{ taxDisplayPolicy.wetLabel }}:</span>
                        </v-col>
                        <v-col
                          v-if="
                            usesAustralianTaxLabels && taxDisplayPolicy.showWet
                          "
                          cols="3"
                          class="text-right pl-2"
                        >
                          <span>{{
                            $filters.currency(paymentAdjustment.totalWet)
                          }}</span>
                        </v-col>
                        <v-col
                          v-if="usesAustralianTaxLabels"
                          cols="9"
                          class="text-right"
                        >
                          <span
                            >{{
                              taxDisplayPolicy.primaryTaxLabel.toUpperCase()
                            }}:</span
                          >
                        </v-col>
                        <v-col
                          v-if="usesAustralianTaxLabels"
                          cols="3"
                          class="text-right pl-2"
                        >
                          <span>{{
                            $filters.currency(paymentAdjustment.totalGst)
                          }}</span>
                        </v-col>
                      </v-row>

                      <div class="i-total-divider"></div>

                      <v-row no-gutters>
                        <v-col cols="9" class="text-right">
                          <span>{{
                            usesAustralianTaxLabels
                              ? 'TOTAL (INC TAX):'
                              : 'TOTAL:'
                          }}</span>
                        </v-col>
                        <v-col cols="3" class="text-right pl-2">
                          <span>{{ $filters.currency(displayTotal) }}</span>
                        </v-col>
                      </v-row>
                    </div>
                  </v-col>
                </v-row>
              </v-col>

              <v-spacer></v-spacer>

              <v-col
                cols="12"
                v-if="paymentAdjustment.comment"
                class="text-left"
              >
                <span class="g-bold">Comment</span>
                <div class="g-border-radius-s i-message">
                  <span>{{ paymentAdjustment.comment }}</span>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-2"></v-divider>
          </v-card>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
  fetchPaymentAdjustment,
  fetchPaymentAdjustmentPdfUrl
} from '@/services/paymentAdjustment'
import iOverlay from '@/components/Overlay'
import { openFile } from '@/utils/common'
import iPaymentType from '@/components/PaymentType'
import { pickSupplierAmount } from '@/utils/supplierAmountDisplay'

export default {
  components: {
    iOverlay,
    iPaymentType
  },

  props: {
    paymentAdjustmentId: {
      required: true,
      type: String
    }
  },

  data: () => ({
    paymentAdjustment: {},
    fetching: false,
    fetchingPDF: false
  }),

  computed: {
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels',
      'taxDisplayPolicy'
    ]),

    displayTotal() {
      return pickSupplierAmount({
        tenantCountryRegion: this.tenantCountryRegion,
        exTax: this.paymentAdjustment.totalExTax,
        incTax: this.paymentAdjustment.totalIncTax
      })
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    fetch() {
      this.fetching = true

      fetchPaymentAdjustment(this.paymentAdjustmentId)
        .then((paymentAdjustment) => {
          this.paymentAdjustment = paymentAdjustment
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetching = false
        })
    },

    fetchPDF() {
      this.fetchingPDF = true
      fetchPaymentAdjustmentPdfUrl(this.paymentAdjustmentId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Adjustment ${this.paymentAdjustmentId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.fetchingPDF = false
        })
    },

    createLinkForRelatedIssue(paymentAdjustment) {
      if (paymentAdjustment.linkedId) {
        const objectId = paymentAdjustment.linkedId.toString()
        switch (paymentAdjustment.linkedType) {
          case 'SUPPLIER_ORDER':
            return {
              name: 'purchase-order-details',
              params: { id: objectId }
            }

          case 'SUPPLIER_ORDER_QUERY':
            return {
              name: 'order-query-details',
              params: { id: objectId }
            }

          case 'CREDIT_RETURN':
            return {
              name: 'credit-return-details',
              params: { id: objectId }
            }

          case 'PAYMENT_ADJUSTMENT':
            return {
              name: 'payment-adjustment-details',
              params: { paymentAdjustmentId: objectId }
            }
        }
      } else {
        return null
      }
    }
  },

  mounted() {
    this.fetch()
  },

  watch: {
    '$route.params'({ paymentAdjustmentId }) {
      this.paymentAdjustmentId = paymentAdjustmentId
      this.fetch()
    }
  }
}
</script>

<style lang="scss" scoped>
#payment-adjustment-details {
  table {
    border-collapse: collapse;

    tr > td {
      padding: 2px 0px;
    }

    tr > td:nth-of-type(1) {
      font-weight: bold;
    }
  }

  .p-div-title {
    background-color: $primary-color-light;
    color: $primary-color;
  }

  .p-title {
    display: inline-block;
    letter-spacing: 0.75px;
  }
}
</style>
